import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@reflex/ui/lib/utils';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { SidebarHeader } from '@reflex/ui/components/sidebar/SidebarHeader';
import { SidebarSection } from '@reflex/ui/components/sidebar/SidebarSection';
import { BlueprintFilterBar } from './BlueprintFilterBar';
import { ScrollArea } from '@reflex/ui/components/ui/scroll-area';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SidebarItem } from '@reflex/ui/components/sidebar/SidebarItem';
import type { MenuActionItem } from '@reflex/ui/components/sidebar/SidebarItem';
import {
  Plus,
  Trash2,
  Pencil,
  RefreshCw,
  CheckCircle2,
  Copy,
  XCircle,
  GitBranch,
  Box,
  AlertTriangle,
  Camera,
} from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { CollapseSidebarButton } from '@reflex/ui/components/sidebar/CollapseSidebarButton';
import { CollapsedSidebarRail } from '@/components/sidebar/CollapsedSidebarRail';
import { CanGuard } from '@reflex/ui/client/permissions';
import {
  useBlueprints,
  useDeleteBlueprint,
  useRebuildBlueprint,
} from '@/hooks/blueprints/useBlueprints';
import { useBlueprintFilters } from '@/hooks/blueprints/useBlueprintFilters';
import { useSnapshots, useDeleteSnapshot } from '@/hooks/snapshots/useSnapshots';
import { BLUEPRINT_STATUS_CONFIG } from '@/lib/status-colors';
import { timeAgo } from '@reflex/ui/lib/format';
import type { Blueprint, BlueprintStatus, Snapshot } from '@/lib/types';
import { isBaseBlueprint } from '@runloop/reflex-contract';
import {
  displayBlueprintName,
  PUBLIC_BASE_BLUEPRINT_NAME,
  DEVBOX_SNAPSHOTS_CAPABILITY,
} from '@reflex/shared';
import { useHasCapability } from '@/hooks/auth/useHasCapability';
import {
  getLatestByName,
  BLUEPRINT_LOAD_ERROR_TITLE,
  BLUEPRINT_LOAD_ERROR_DESCRIPTION,
} from '@/lib/blueprints';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { ArchBadge } from '@reflex/ui/components/shared/ArchBadge';

const EMPTY_BLUEPRINTS: Blueprint[] = [];

function StatusIcon({ status }: { status: BlueprintStatus }) {
  switch (status) {
    case 'queued':
    case 'provisioning':
    case 'building':
      return <Spinner className="h-4 w-4 text-status-info" />;
    case 'build_complete':
      return <CheckCircle2 className="h-4 w-4 shrink-0 text-status-success" />;
    case 'failed':
      return <XCircle className="h-4 w-4 shrink-0 text-status-error" />;
  }
}

const EMPTY_SNAPSHOTS: Snapshot[] = [];

interface BlueprintSidebarProps {
  selectedId: string | null;
  onSelect: (blueprint: Blueprint) => void;
  onSelectSnapshot: (snapshot: Snapshot) => void;
  onDeselect: () => void;
  collapsed: boolean;
  onToggle: () => void;
  mobile?: boolean;
}

export function BlueprintSidebar({
  selectedId,
  onSelect,
  onSelectSnapshot,
  onDeselect,
  collapsed,
  onToggle,
  mobile,
}: BlueprintSidebarProps) {
  const navigate = useNavigate();
  const blueprintsQuery = useBlueprints();
  const liveLoadFailed = blueprintsQuery.isFallback && blueprintsQuery.source === 'live-fallback';
  const blueprints = liveLoadFailed ? EMPTY_BLUEPRINTS : (blueprintsQuery.data ?? EMPTY_BLUEPRINTS);
  // Snapshots are an org entitlement (the `devbox-snapshots` capability) — when
  // the org lacks it, skip the fetch and hide the section entirely. The server
  // also returns `[]` for these orgs, but gating here avoids the empty request
  // and mirrors the launch picker's gate.
  const canUseSnapshots = useHasCapability(DEVBOX_SNAPSHOTS_CAPABILITY);
  const snapshots = useSnapshots({ enabled: canUseSnapshots }).data ?? EMPTY_SNAPSHOTS;
  const routes = useOrgRoutes();
  const deleteBlueprint = useDeleteBlueprint();
  const rebuildBlueprint = useRebuildBlueprint();
  const deleteSnapshot = useDeleteSnapshot();
  const blueprintFilters = useBlueprintFilters();

  // Snapshots carry no build status, so the status filter doesn't apply —
  // they show only when no status filter is active, narrowed by the search.
  const filteredSnapshots = useMemo(() => {
    // Gate the rendered list directly, not just the fetch: a disabled query
    // still returns any previously-cached snapshots, so the capability check
    // has to bound what the section renders.
    if (!canUseSnapshots) return EMPTY_SNAPSHOTS;
    if (blueprintFilters.filters.status) return EMPTY_SNAPSHOTS;
    const needle = blueprintFilters.filters.search.toLowerCase();
    return needle ? snapshots.filter((s) => s.name.toLowerCase().includes(needle)) : snapshots;
  }, [
    canUseSnapshots,
    snapshots,
    blueprintFilters.filters.status,
    blueprintFilters.filters.search,
  ]);

  const latestBlueprints = useMemo(() => getLatestByName(blueprints), [blueprints]);
  const filteredBlueprints = useMemo(
    () => blueprintFilters.filterBlueprints(latestBlueprints),
    [blueprintFilters, latestBlueprints],
  );

  // Two-section view: bases on top, everything else below. `isBaseBlueprint`
  // is the canonical shared rule (metadata.type='base' OR name 'base' OR
  // name contains '_base') — see shared/src/blueprint.types.ts. The
  // collapsed rail mirrors the partition order without rendering headers.
  const partitioned = useMemo(() => {
    const bases: Blueprint[] = [];
    const other: Blueprint[] = [];
    for (const bp of filteredBlueprints) {
      (isBaseBlueprint(bp) ? bases : other).push(bp);
    }
    return { bases, other };
  }, [filteredBlueprints]);
  const railOrdered = useMemo(() => {
    const bases: Blueprint[] = [];
    const other: Blueprint[] = [];
    for (const bp of latestBlueprints) {
      (isBaseBlueprint(bp) ? bases : other).push(bp);
    }
    return [...bases, ...other];
  }, [latestBlueprints]);

  const [pendingDelete, setPendingDelete] = useState<Blueprint | null>(null);

  function confirmDelete() {
    if (!pendingDelete) return;
    const bp = pendingDelete;
    deleteBlueprint.mutate(bp.id, {
      onSuccess: () => {
        toast.success('Blueprint deleted');
        if (selectedId === bp.id) onDeselect();
        setPendingDelete(null);
      },
      onError: (err: Error) => {
        toast.error('Failed to delete blueprint', { description: err.message });
        setPendingDelete(null);
      },
    });
  }

  function handleClone(blueprint: Blueprint) {
    navigate(routes.blueprintNew, { state: { clone: blueprint } });
  }

  function handleEdit(blueprint: Blueprint) {
    navigate(routes.blueprintNew, { state: { clone: blueprint } });
  }

  function handleRebuild(blueprint: Blueprint) {
    // Server-side replay from the stored parameters keeps every build input,
    // including build args and secrets the client can't fully see.
    rebuildBlueprint.mutate(blueprint.id, {
      onSuccess: () =>
        toast.success('Rebuild started', {
          description: 'A fresh build has been queued with the same configuration.',
        }),
      onError: (err: Error) =>
        toast.error('Failed to rebuild blueprint', { description: err.message }),
    });
  }

  function handleDeleteSnapshot(snapshot: Snapshot) {
    deleteSnapshot.mutate(snapshot.id, {
      onSuccess: () => {
        toast.success('Snapshot deleted');
        if (selectedId === snapshot.id) onDeselect();
      },
      onError: (err: Error) =>
        toast.error('Failed to delete snapshot', { description: err.message }),
    });
  }

  function renderSnapshotItem(snap: Snapshot) {
    return (
      <motion.div key={snap.id} {...staggerItem}>
        <SidebarItem
          testId={`snapshot-item-${snap.id}`}
          selected={selectedId === snap.id}
          onClick={() => onSelectSnapshot(snap)}
          icon={<Camera className="h-4 w-4 shrink-0 text-muted-foreground" />}
          menuActions={
            [
              {
                label: 'Delete',
                icon: Trash2,
                onClick: () => handleDeleteSnapshot(snap),
                destructive: true,
              },
            ] satisfies MenuActionItem[]
          }
        >
          <div className="flex items-baseline justify-between gap-2">
            <PanelTitle as="h3" size="sm">
              {snap.name}
            </PanelTitle>
            <span className="shrink-0 text-3xs text-muted-foreground">
              {timeAgo(snap.createTimeMs, { compact: true })}
            </span>
          </div>
          {snap.sourceAgentName && (
            <p className="text-2xs truncate text-muted-foreground">{snap.sourceAgentName}</p>
          )}
        </SidebarItem>
      </motion.div>
    );
  }

  function renderBlueprintItem(bp: Blueprint) {
    const label = BLUEPRINT_STATUS_CONFIG[bp.status].sidebar;
    // The bootstrap-managed `base` singleton and the shared public base
    // are host-managed: cloning either would produce a fork the bootstrap
    // service won't maintain, and deleting is blocked server-side (see
    // blueprint.routes.ts). Hide both actions so the UI matches what the
    // API will accept; base customization goes through the organization
    // overrides on the detail panel. Other bases (metadata.type='base'
    // or `_base`-named) behave normally.
    const isHostManagedBase = bp.name === 'base' || bp.name === PUBLIC_BASE_BLUEPRINT_NAME;

    return (
      <motion.div
        key={bp.id}
        data-tutorial-id={isHostManagedBase ? 'blueprint-base-item' : undefined}
        {...staggerItem}
      >
        <SidebarItem
          testId={`blueprint-item-${bp.id}`}
          selected={selectedId === bp.id}
          onClick={() => onSelect(bp)}
          icon={<StatusIcon status={bp.status} />}
          actions={
            isHostManagedBase ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Clone"
                    className="p-1 rounded hover:text-foreground"
                    onClick={() => handleClone(bp)}
                  >
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">Clone</TooltipContent>
              </Tooltip>
            )
          }
          menuActions={
            isHostManagedBase
              ? undefined
              : ([
                  { label: 'Edit', icon: Pencil, onClick: () => handleEdit(bp) },
                  { label: 'Rebuild', icon: RefreshCw, onClick: () => handleRebuild(bp) },
                  { separator: true as const },
                  {
                    label: 'Delete',
                    icon: Trash2,
                    onClick: () => setPendingDelete(bp),
                    destructive: true,
                  },
                ] satisfies MenuActionItem[])
          }
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <PanelTitle as="h3" size="sm" className="truncate">
                {displayBlueprintName(bp.name)}
              </PanelTitle>
              <ArchBadge architecture={bp.architecture} hideDefault />
            </div>
            <span className="shrink-0 text-3xs text-muted-foreground">
              {timeAgo(bp.createTimeMs, { compact: true })}
            </span>
          </div>
          <p className={cn('text-2xs truncate', label.className)}>{label.text}</p>
          {bp.metadata?.repo && (
            <div className="mt-0.5 flex items-center gap-1 text-3xs text-muted-foreground">
              <GitBranch className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">{bp.metadata.repo}</span>
            </div>
          )}
        </SidebarItem>
      </motion.div>
    );
  }

  if (collapsed && !mobile) {
    return (
      <CollapsedSidebarRail onToggle={onToggle}>
        <div className="mt-2 flex flex-col items-center gap-3">
          {liveLoadFailed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => navigate(routes.secrets)}
                  aria-label="Blueprints failed to load"
                  className="rounded-md p-1.5 text-status-error transition-colors hover:bg-sidebar-accent/50"
                >
                  <AlertTriangle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-56">
                <p className="font-medium">{BLUEPRINT_LOAD_ERROR_TITLE}</p>
                <p className="text-muted-foreground">{BLUEPRINT_LOAD_ERROR_DESCRIPTION}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {railOrdered.map((bp) => (
            <Tooltip key={bp.id}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onSelect(bp)}
                  aria-label={`${displayBlueprintName(bp.name)} - ${BLUEPRINT_STATUS_CONFIG[bp.status].sidebar.text}`}
                  className={cn(
                    'rounded-md p-1.5 transition-colors',
                    selectedId === bp.id
                      ? 'bg-selected ring-1 ring-selected-border/40'
                      : 'hover:bg-sidebar-accent/50',
                  )}
                >
                  <StatusIcon status={bp.status} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-56">
                <p className="font-medium">{displayBlueprintName(bp.name)}</p>
                <p className="text-muted-foreground">
                  {BLUEPRINT_STATUS_CONFIG[bp.status].sidebar.text}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </CollapsedSidebarRail>
    );
  }

  return (
    <div
      data-tutorial-id="blueprint-sidebar"
      className={cn(
        'flex h-full w-full flex-col overflow-hidden',
        mobile ? 'bg-background' : 'bg-panel',
      )}
    >
      <SidebarHeader
        title="Blueprints"
        docs="concepts/blueprints"
        actions={
          <>
            <CanGuard permission="blueprints:write">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => navigate(routes.blueprintNew)}
                    aria-label="New blueprint"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>New Blueprint</TooltipContent>
              </Tooltip>
            </CanGuard>
            <CollapseSidebarButton onClick={onToggle} />
          </>
        }
      >
        <div data-tutorial-id="blueprint-filter-bar" className="px-3 py-2">
          <BlueprintFilterBar filtersResult={blueprintFilters} />
        </div>
      </SidebarHeader>

      <ScrollArea className="flex-1 min-h-0">
        <div className="pb-2">
          {liveLoadFailed && (
            <div className="px-3 py-4" data-testid="blueprint-sidebar-load-error">
              <EmptyState
                icon={<AlertTriangle className="h-5 w-5 text-status-error" />}
                title={BLUEPRINT_LOAD_ERROR_TITLE}
                description={blueprintsQuery.error?.message ?? BLUEPRINT_LOAD_ERROR_DESCRIPTION}
                action={
                  <Button size="sm" variant="outline" onClick={() => navigate(routes.secrets)}>
                    Open Secrets
                  </Button>
                }
              />
            </div>
          )}
          {!liveLoadFailed && !filteredBlueprints.length && !filteredSnapshots.length && (
            <EmptyState icon={<Box className="h-5 w-5" />} title="No blueprints yet" />
          )}
          <motion.div {...staggerContainer}>
            <AnimatePresence initial={false}>
              {partitioned.other.length > 0 && (
                <SidebarSection
                  key="section-other"
                  label="Blueprints"
                  data-testid="blueprint-section-other"
                  triggerTestId="blueprint-section-toggle-other"
                >
                  {partitioned.other.map((bp) => renderBlueprintItem(bp))}
                </SidebarSection>
              )}
              {partitioned.bases.length > 0 && (
                <SidebarSection
                  key="section-base"
                  label="Base"
                  data-testid="blueprint-section-base"
                  triggerTestId="blueprint-section-toggle-base"
                >
                  {partitioned.bases.map((bp) => renderBlueprintItem(bp))}
                </SidebarSection>
              )}
              {filteredSnapshots.length > 0 && (
                <SidebarSection
                  key="section-snapshots"
                  label="Snapshots"
                  data-testid="blueprint-section-snapshots"
                  triggerTestId="blueprint-section-toggle-snapshots"
                >
                  {filteredSnapshots.map((snap) => renderSnapshotItem(snap))}
                </SidebarSection>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </ScrollArea>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {pendingDelete ? displayBlueprintName(pendingDelete.name) : 'blueprint'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This blueprint will be permanently deleted. Any agents or personas currently using it
              will fail to launch until they are reconfigured with a different blueprint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete blueprint
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
